import { useContext } from 'react';
import * as React from 'react';
import styled, { useTheme } from 'styled-components';
import { AuthContext } from '@sdk/authentication/AuthContext';
import { isRequest } from '@sdk/unavailabilities/helpers/utils';
import EventBoxContainerStyle, {
  EventBoxContainerProps,
} from '@sdk/schedule/components/EventBoxContainerStyle';
import BoxIcon from '@sdk/shifts/components/BoxIcon';
import { EventBoxCommonBaseProps } from '@sdk/schedule/types/EventBoxProps';
import EventBoxOuter from '@sdk/schedule/components/EventBoxOuter';
import EventBoxInner from '@sdk/schedule/components/EventBoxInner';
import type { UnavailabilityDisplayType } from '@sdk/unavailabilities/types/UnavailabilityType';

import UnavailabilityIcon from '../assets/svg/Icons/24/unavailability-white.svg';

import type { OpenEventCallback } from './types';
import FlexRow from './ui/FlexRow';
import getRenderBoxLine from './helpers/getRenderBoxLine';

const Box = styled(FlexRow)<EventBoxContainerProps>`
  ${EventBoxContainerStyle}

  background-color: ${({ theme }): string => theme.colors.unavailability};
  border: 2px solid ${({ theme }): string => theme.colors.unavailability};

  ${EventBoxOuter}:hover & {
    background-color: ${({ theme }): string => theme.colors.unavailabilityHover};
    border-color: ${({ theme }): string => theme.colors.unavailabilityHover};
  }
`;

type Props = {
  unavailability: UnavailabilityDisplayType;
  openEvent: OpenEventCallback;
} & EventBoxCommonBaseProps;

const UnavailabilityBox: React.FC<React.PropsWithChildren<Props>> = ({
  hoverComponent,
  unavailability,
  openEvent,
  shiftAppearanceMode,
  height,
  showPlus,
}) => {
  const { currentUser } = useContext(AuthContext);
  const { colors } = useTheme();
  const { week } = unavailability.shiftAppearance;
  const isPending = isRequest(unavailability, currentUser.orgSettings);
  const shouldDisplayThirdLine = shiftAppearanceMode === 'month' && week.thirdLine;

  const onClick = (evt: React.MouseEvent<HTMLDivElement>): void => {
    openEvent({ event: unavailability, isExchange: false, $event: evt });
  };

  const renderBoxLine = getRenderBoxLine({
    color: colors.primaryText,
    hoverColor: colors.white,
    hoverComponent: EventBoxOuter,
  });

  return (
    <EventBoxOuter>
      <Box hoverComponent={hoverComponent} showPlus={showPlus} onClick={onClick} height={height}>
        <EventBoxInner>
          {renderBoxLine(week.firstLine, true)}

          {week.secondLine && renderBoxLine(week.secondLine)}
          {shouldDisplayThirdLine && renderBoxLine(week.thirdLine)}
          {week.fourthLine && renderBoxLine(week.fourthLine)}
        </EventBoxInner>
        {isPending && <BoxIcon source={UnavailabilityIcon} color={colors.white} />}
      </Box>
    </EventBoxOuter>
  );
};

export default UnavailabilityBox;
